import { Grid, Typography } from "@mui/material";

import LinearProgress, { LinearProgressProps } from "@mui/material/LinearProgress";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Backfill } from "../../graphql/types";
import { toTitleCase } from "../../lib/Util";
import { ProgressBarWaiting } from "../ui/ProgressBars";
import { Badge } from "../ui/badges";

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Grid container spacing={1} sx={{ alignItems: "center" }}>
      <Grid size={8} data-testid={`progressbar-color-${props.color}`}>
        <LinearProgress variant="determinate" {...props} />
      </Grid>
      <Grid>
        <Typography variant="body2" sx={{ color: "text.secondary" }} data-cy="progress-percentage">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Grid>
    </Grid>
  );
}

type IBackfillProgressLinear = Pick<
  Backfill,
  "succeeded_pipelines" | "failed_pipelines" | "total_pipelines" | "status"
>;

const BackfillProgressLinear = ({
  succeeded_pipelines,
  failed_pipelines,
  total_pipelines,
  status,
}: IBackfillProgressLinear) => {
  const succeeded = succeeded_pipelines || 0;
  const failed = failed_pipelines || 0;

  const progress = total_pipelines ? ((succeeded + failed) * 100) / total_pipelines : 0;

  return (
    <>
      {status === "preparing" ? (
        <ProgressBarWaiting badgeText={toTitleCase(status)} status={status} />
      ) : (
        <LinearProgressWithLabel
          value={progress}
          color={failed ? "warning" : "info"}
          test-label="linear-progress-with-label"
        />
      )}
    </>
  );
};

type PipelineCount = number | null | undefined;

const BackfillStatus = ({
  totalPipelines,
  failedPipelines,
  succeededPipelines,
  cancelledPipelines,
  status,
  hasDirtyRows,
}: {
  totalPipelines: PipelineCount;
  succeededPipelines: PipelineCount;
  failedPipelines: PipelineCount;
  cancelledPipelines: PipelineCount;
  status: string;
  hasDirtyRows?: boolean;
}) => {
  if (status === "cancelled" || (cancelledPipelines && cancelledPipelines > 0)) {
    return <Badge icon={<WarningIcon />} size="small" severity="error" label="Cancelled" data-cy="cancelled-status" />;
  }
  if (status === "completed") {
    if (!totalPipelines) {
      /* The backfill failed to run.*/
      return (
        <Badge
          icon={<WarningIcon />}
          size="small"
          severity="error"
          label="Failed to run"
          data-cy="failed-to-run-status"
        />
      );
    }
    if (failedPipelines) {
      if (failedPipelines === totalPipelines) {
        return (
          <Badge
            icon={<WarningIcon />}
            size="small"
            severity="error"
            label="All pipelines failed"
            data-cy="all-pipelines-failed-status"
          />
        );
      }
      return (
        <Badge
          icon={<WarningIcon />}
          size="small"
          severity="warning"
          label={`${failedPipelines} out of ${totalPipelines} pipelines failed`}
          data-cy="chip-completed-with-errors"
        />
      );
    }
    if (hasDirtyRows) {
      return (
        <Badge
          icon={<WarningIcon />}
          size="small"
          severity="warning"
          label="Completed with dirty rows"
          data-cy="chip-completed-with-errors"
        />
      );
    }

    return (
      <Badge icon={<CheckCircleIcon />} size="small" severity="success" label="Completed" data-cy="chip-completed" />
    );
  }

  return (
    <BackfillProgressLinear
      status={status}
      succeeded_pipelines={succeededPipelines}
      failed_pipelines={failedPipelines}
      total_pipelines={totalPipelines}
    />
  );
};

export default BackfillStatus;
